import { CommonModule, NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, effect, EnvironmentInjector, inject, OnInit, signal, viewChild } from "@angular/core";
import { IonicModule, IonTabs, isPlatform, NavController, Platform, ToastController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { loadCustomer } from "../auth/ngrx/customer.actions";
import {
  getCalendarResources,
  loadAppointments,
  updateCalendarResource,
} from "../calendar/ngrx/calendar.actions";
import { loadInvoices } from "../office/invoice-ngrx/invoices.actions";
import { loadStudents } from "../students/ngrx/students.actions";
import { loadPayments } from "../office/payment-ngrx/payments.actions";
import {
  loadEducationBodies,
  loadEducationCategories,
  loadEducationTitles,
  loadLessonRatings,
  loadServices,
  loadSubscriptions,
  selectSetting,
} from "../settings/ngrx/settings.action";
import { GcalendarService } from "../shared/services/gcalendar.service";
import * as resourceSelector from "../calendar/ngrx/resources.selectors";
import * as calendarSelectors from "../calendar/ngrx/calendar.selectors";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CalendarResourceDto } from "../calendar/interfaces/calendar_resource_dto";
import {
  distinctUntilKeyChanged,
  Subject,
  takeUntil,
} from "rxjs";
import { getTrackings } from "../tracking/ngrx/tracking.actions";
import {
  loadReportConfigurations,
  loadReports,
} from "../report-generation/ngrx/reports.actions";
import { AuthService } from "../supabase/services/auth.service";
import { selectCustomer } from "../auth/ngrx/customer.selectors";
import { LocalCalendarService } from "../shared/services/localcalendar.service";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { loadProducts } from "../products/ngrx/products.actions";

@Component({
    selector: "app-home",
    templateUrl: "home.page.html",
    styleUrls: ["home.page.scss"],
    imports: [
        IonicModule,
        NgClass,
        FormsModule,
        ReactiveFormsModule,
        CommonModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomePage implements OnInit {
  private store = inject<Store<any>>(Store);
  private gcalendarService = inject(GcalendarService);
  private authService = inject(AuthService);
  private localCalendarService = inject(LocalCalendarService);
  private toastController = inject(ToastController);
  private navController = inject(NavController);
  private platform = inject(Platform);

  public environmentInjector = inject(EnvironmentInjector);
  public isDesktop =
    isPlatform("desktop") && !isPlatform("android") && !isPlatform("ios");
  public currentTab = signal<string>("tab0");
  wasPaused = signal<Date | null>(null);
  resources = signal<CalendarResourceDto[]>([]);
  calendarResources$ = this.store.select(resourceSelector.selectAllResources);
  customer$ = this.store.select(selectCustomer);
  appointmentsSignal = this.store.selectSignal(
    calendarSelectors.selectAllAppointments
  );

  tab0Clicked = signal<boolean>(false);
  tab1Clicked = signal<boolean>(false);
  tab2Clicked = signal<boolean>(false);
  tab3Clicked = signal<boolean>(false);
  tab4Clicked = signal<boolean>(false);

  private destroy$: Subject<void> = new Subject<void>();

  constructor() {
    this.calendarResources$
      .pipe(takeUntil(this.destroy$))
      .subscribe((resources) => {
        this.resources.set(JSON.parse(JSON.stringify(resources)));
      });

    this.customer$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (customer) => {
        if (customer.local_calendar_connected && isPlatform("capacitor")) {
          const permission = await this.localCalendarService.checkPermissions();
          if (!permission) {
            const grantPermission = this.localCalendarService.requestPermissions();
            if (!grantPermission) {
              const toast = await this.toastController.create({
                message: "Fehler beim Verbinden des Lokalen Kalenders",
                duration: 2000,
                position: "bottom",
                color: "danger",
              });
              await toast.present();
            }
          }
        }
      })
  }

  async ngOnInit() {
    if (isPlatform("capacitor")) {
      Keyboard.setResizeMode({
        mode: KeyboardResize.Ionic
      });
    }
    document?.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.wasPaused.set(new Date());
      }
    });
    this.authService
      .getCurrentUser()
      .pipe(distinctUntilKeyChanged("id"), takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user && typeof user === "object" && "id" in user && (this.wasPaused() === null || (new Date().getTime() - this.wasPaused().getTime()) > 30 * 60 * 1000)) {
          this.store.dispatch(loadCustomer());
          this.store.dispatch(loadAppointments());
          this.store.dispatch(loadInvoices());
          this.store.dispatch(loadStudents());
          this.store.dispatch(loadServices());
          this.store.dispatch(loadProducts());
          this.store.dispatch(loadSubscriptions());
          this.store.dispatch(loadPayments());
          this.store.dispatch(loadEducationCategories());
          this.store.dispatch(loadLessonRatings());
          this.store.dispatch(getCalendarResources());
          this.store.dispatch(getTrackings());
          this.store.dispatch(loadReports());
          this.store.dispatch(loadReportConfigurations());
          this.store.dispatch(loadEducationCategories());
          this.store.dispatch(loadEducationBodies());
          this.store.dispatch(loadEducationTitles());
          this.store.dispatch(selectSetting({ setting: "company" }));
        }
      });

    this.gcalendarService.refreshGoogleToken();
  }

  setCurrentTab(ev: any) {
    this.currentTab.set(ev.tab);
  }

  changeCalendarResource(resource: CalendarResourceDto) {
    this.store.dispatch(updateCalendarResource({ resource }));
  }

  navigateToCalendarSettings() {
    let path = "calendar";
    this.store.dispatch(selectSetting({ setting: "calendar" }));
    if (this.platform.width() >= 700) {
      path = "/home/tab4";
    }
    this.navController.navigateForward([path]);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
